/**
 * WHAT AN IMAGE UPLOAD MAY BE — one list, because it was six.
 *
 * The accepted types were written out by hand in the Image block (a check and an `accept`), the
 * Library modal (a check and three `accept` strings), and the backend twice over. Adding
 * `image/gif` to some of them is exactly what happened: the server accepted it, the Image block's
 * check allowed it, and both file pickers still filtered it out of the dialog — so the format was
 * supported and unselectable at the same time.
 *
 * ## This mirrors the server, it does not decide
 *
 * The authority is `STORAGE_ALLOWED_IMAGE_MIME_TYPES` in the backend's `env.validation.ts`, whose
 * Zod default wins over the `||` fallback beside it. These constants exist so the UI can refuse
 * early and politely; the server refuses regardless. When one changes, change both.
 */

/** Keep in step with `STORAGE_ALLOWED_IMAGE_MIME_TYPES`. */
export const ALLOWED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;

/** The same list as a file input's `accept`. Derived, so a picker can never offer a different set
 *  from the one the check allows — which is the bug this file exists for. */
export const IMAGE_ACCEPT = ALLOWED_IMAGE_MIME_TYPES.join(',');

/**
 * Types that MAY carry more than one frame.
 *
 * Not a claim that a given file does — only decoding answers that — but it is what the byte cap
 * keys on, since the cap is applied before the file is read. Mirrors the server's own
 * `ANIMATABLE_MIME_TYPES`.
 */
export const ANIMATABLE_IMAGE_MIME_TYPES: readonly string[] = ['image/webp', 'image/gif'];

/** Keep in step with `STORAGE_MAX_UPLOAD_BYTES`. */
export const MAX_IMAGE_UPLOAD_BYTES = 8 * 1024 * 1024;

/** Keep in step with `STORAGE_ANIMATED_MAX_UPLOAD_BYTES`. See that entry for why 2 MB. */
export const MAX_ANIMATED_UPLOAD_BYTES = 2 * 1024 * 1024;

/** The cap this file is held to, by the same rule the server applies. */
export function maxUploadBytesFor(mimeType: string): number {
  return ANIMATABLE_IMAGE_MIME_TYPES.includes(mimeType) ? MAX_ANIMATED_UPLOAD_BYTES : MAX_IMAGE_UPLOAD_BYTES;
}

export function isAllowedImageMimeType(mimeType: string): boolean {
  return (ALLOWED_IMAGE_MIME_TYPES as readonly string[]).includes(mimeType);
}
